/**
 * Utility to reliably extract the human-readable error message from backend API responses,
 * Axios error objects, native fetch errors, or string messages.
 */
export function extractErrorMessage(error, fallback = "An unexpected error occurred. Please try again.") {
  if (!error) return fallback;

  // If already a simple string
  if (typeof error === "string") {
    const trimmed = error.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  }

  // Check common API response structures:
  // e.g. error.data, error.response.data, error.message, error.error
  const resData = error.response?.data || error.data || error;

  // 1. Direct message / error / msg string properties
  if (typeof resData === "string") {
    try {
      const parsed = JSON.parse(resData);
      return extractErrorMessage(parsed, fallback);
    } catch (_) {
      return resData.trim() || fallback;
    }
  }

  if (resData && typeof resData === "object") {
    // Standard backend properties: message, error, msg, detail, description
    if (typeof resData.message === "string" && resData.message.trim()) {
      return resData.message.trim();
    }
    if (typeof resData.error === "string" && resData.error.trim()) {
      return resData.error.trim();
    }
    if (typeof resData.msg === "string" && resData.msg.trim()) {
      return resData.msg.trim();
    }
    if (typeof resData.detail === "string" && resData.detail.trim()) {
      return resData.detail.trim();
    }
    if (typeof resData.description === "string" && resData.description.trim()) {
      return resData.description.trim();
    }

    // Nested data object: e.g. { data: { error: "..." } } or { data: { message: "..." } }
    if (resData.data && typeof resData.data === "object") {
      const nestedMsg = extractErrorMessage(resData.data, null);
      if (nestedMsg) return nestedMsg;
    }

    // Validation errors object / array e.g. { errors: { phone: ["Already registered"] } }
    if (resData.errors) {
      if (typeof resData.errors === "string") return resData.errors;
      if (Array.isArray(resData.errors) && resData.errors.length > 0) {
        return extractErrorMessage(resData.errors[0], fallback);
      }
      if (typeof resData.errors === "object") {
        const firstKey = Object.keys(resData.errors)[0];
        if (firstKey) {
          const val = resData.errors[firstKey];
          return Array.isArray(val) ? val[0] : String(val);
        }
      }
    }
  }

  // 2. Axios / JavaScript Error object message
  if (error.message && typeof error.message === "string") {
    // Avoid showing raw generic network strings if possible
    if (error.message.includes("Network Error")) {
      return "Unable to connect to server. Please check your internet connection.";
    }
    return error.message;
  }

  return fallback;
}
